import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { pipe, find, propEq, propOr, max, dec, min, inc, not, pick } from 'ramda';
import PropTypes from 'prop-types';

import { useDiagnosesActions, useAlerts } from 'store/diagnoses';
import { FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE, ORIGINAL_NAME } from 'store/examinations';
import { extractImage } from 'utils';
import ImagesReview from 'components/ImagesReview';
import Spinner from 'components/FullScreenSpinner';
import ArrowRight from 'assets/svg-icons/ArrowRight';
import { LeftArrow, RightArrow } from '../styles';

import { ImagePaginationContainer, ImagesSection, EyeImage, SpinnerWrap } from './styles';

const Images = ({ items, currentId }) => {
  const { loadAnalysisImages } = useDiagnosesActions();
  const { action, loading, success } = useAlerts(loadAnalysisImages);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isOpenReview, setIsOpenReview] = useState();
  const currentExamImages = useMemo(
    () => pipe(find(propEq(currentId, FILE_ID)), propOr([], 'files'), extractImage)(success || []),
    [currentId, success]
  );
  const counter = useMemo(
    () => (currentExamImages.length ? `${activeImageIndex + 1}/${currentExamImages.length}` : '0/0'),
    [currentExamImages, activeImageIndex]
  );
  const allImages = useMemo(
    () =>
      (success || []).reduce((acc, { id, date, files }) => {
        files.forEach((file) => {
          acc.push({
            ...pick([FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE, ORIGINAL_NAME], file),
            date,
            isCurrent: id === currentId,
          });
        });

        return acc;
      }, []),
    [success, currentId]
  );

  const handleClick = useCallback(
    ({ currentTarget }) => {
      const { id } = currentTarget.dataset;
      if (id === 'dec') setActiveImageIndex((val) => max(0, dec(val)));
      if (id === 'inc') setActiveImageIndex((val) => min(currentExamImages.length, inc(val)));
    },
    [currentExamImages]
  );
  const handleReviewClick = useCallback(() => setIsOpenReview(not), []);

  useEffect(() => {
    action(items);
  }, [action, items]);

  return (
    <>
      <ImagesSection>
        <LeftArrow type="button" data-id="dec" disabled={activeImageIndex === 0} onClick={handleClick}>
          <ArrowRight />
        </LeftArrow>
        <EyeImage src={currentExamImages[activeImageIndex]?.url} onClick={handleReviewClick} />
        <RightArrow
          type="button"
          data-id="inc"
          disabled={!currentExamImages.length || activeImageIndex === currentExamImages.length - 1}
          onClick={handleClick}
        >
          <ArrowRight />
        </RightArrow>
        {loading && (
          <SpinnerWrap>
            <Spinner />
          </SpinnerWrap>
        )}
      </ImagesSection>
      <ImagePaginationContainer>{counter}</ImagePaginationContainer>
      {isOpenReview && (
        <ImagesReview
          images={allImages}
          currentImage={currentExamImages[activeImageIndex]?.[FILE_ID] || currentExamImages[activeImageIndex]?.[FILE_URL]}
          onClose={handleReviewClick}
          withCompare
        />
      )}
    </>
  );
};

Images.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      files: PropTypes.arrayOf(
        PropTypes.shape({
          [FILE_ID]: PropTypes.string.isRequired,
          [FILE_URL]: PropTypes.string,
          [FILE_URL_EXPIRES]: PropTypes.string,
          [BUCKET]: PropTypes.string.isRequired,
          [MIMETYPE]: PropTypes.string.isRequired,
          [ORIGINAL_NAME]: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  currentId: PropTypes.string.isRequired,
};

export default Images;
